/**
 * chain-eos/mechat/keys.ts — resolving the public key a message must be encrypted to.
 *
 * `mechatmechat` has no key-registration action (the full NewChat SDK has `crtcfg`,
 * the deployed PayCash contract does not), so the encryption key is the account's
 * ordinary permission key: `active` first, `owner` as a fallback.
 */

import { APIClient } from '@wharfkit/antelope'
import { EOS_RPC_ENDPOINTS } from '../config.js'

export interface RecipientKey {
  account: string
  /** Public key in `PUB_K1_...` form. */
  publicKey: string
  permission: 'active' | 'owner'
  /**
   * True when the permission holds more than one key. The message is encrypted to
   * the highest-weight key only; a recipient whose wallet holds one of the *other*
   * keys will not be able to read it.
   */
  ambiguous: boolean
}

interface PermissionKey {
  key: string
  weight: number
}

interface PermissionRow {
  perm_name: string
  required_auth: {
    threshold: number
    keys: PermissionKey[]
    accounts: unknown[]
  }
}

function pickKey(perm: PermissionRow | undefined): { key: string; ambiguous: boolean } | null {
  const keys = perm?.required_auth?.keys ?? []
  if (keys.length === 0) return null
  const sorted = [...keys].sort((a, b) => Number(b.weight) - Number(a.weight))
  return { key: String(sorted[0].key), ambiguous: keys.length > 1 }
}

/**
 * Looks up the public key to encrypt for. Throws when the account cannot receive
 * messages — i.e. its permissions are delegated to other accounts and hold no keys
 * of their own (common for multisig / contract-controlled accounts).
 */
export async function resolveRecipientKey(
  account: string,
  endpointIndex = 0,
): Promise<RecipientKey> {
  const name = String(account ?? '').trim()
  if (!name) throw new Error('[mechat] empty account name')

  const url = EOS_RPC_ENDPOINTS[endpointIndex % EOS_RPC_ENDPOINTS.length]
  try {
    const client = new APIClient({ url })
    const res = await client.v1.chain.get_account(name)
    const perms = JSON.parse(JSON.stringify(res.permissions ?? [])) as PermissionRow[]

    const active = pickKey(perms.find(p => p.perm_name === 'active'))
    if (active) {
      return { account: name, publicKey: active.key, permission: 'active', ambiguous: active.ambiguous }
    }
    const owner = pickKey(perms.find(p => p.perm_name === 'owner'))
    if (owner) {
      return { account: name, publicKey: owner.key, permission: 'owner', ambiguous: owner.ambiguous }
    }
    throw new Error(`[mechat] account ${name} has no public key on active/owner — cannot receive messages`)
  } catch (err) {
    const isLast = endpointIndex >= EOS_RPC_ENDPOINTS.length - 1
    if (!isLast && !(err instanceof Error && err.message.startsWith('[mechat]'))) {
      return resolveRecipientKey(name, endpointIndex + 1)
    }
    throw err
  }
}
